import { CheckboxOption } from "@/components/Cancellation/data/feedback-options";
import { TProductRefId } from "@/types/plan";
import { create } from "zustand";
import type { StepId } from "./step-navigation";
import { INITIAL_STACK } from "./step-navigation/flow";

type TState = {
  toCancleProductId: string;
  isProcessing: boolean;

  // Stack-based navigation
  navStack: StepId[];
  cancellingProductIds: TProductRefId[];

  openCancellationModal: boolean;
  openCancelConfirmModal: boolean;

  missingFeatures: string[];
  actionType: "cancel" | "downgrade" | "none";

  feedbackData: {
    reasons: string[];
    alternateChoice: string;
    feedback: string;
    subscription_id: string;
    otherfeedback: string;
  };

  isOfferClaimed: boolean;
  isPauseClaimed: boolean;
  isUserAnnualPlan: boolean;
  isRedirecting: boolean;
  redirectPath: string;

  feedbackSubmitted: boolean;

  warningMessages: string[];
  warningTitle: string;
  warningTemplate: 1 | 2;

  shuffledOptions: CheckboxOption[];
};

type TActions = {
  setToCancleProductId: (productId: string) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  setCancellingProductIds: (ids: TProductRefId[]) => void;
  setOpenCancellationModal: (feedbackSubmitted: boolean) => void;
  setOpenCancelConfirmModal: (open: boolean) => void;
  setMissingFeatures: (features: string[]) => void;
  setActionType: (actionType: "cancel" | "downgrade" | "none") => void;
  setFeedbackData: (data: {
    reasons?: string[];
    alternateChoice?: string;
    feedback?: string;
    otherfeedback?: string;
  }) => void;

  // Stack-based navigation actions
  initNavFlow: (startStep: StepId) => void;
  pushNavStep: (step: StepId) => void;
  popNavStep: () => StepId | null;
  currentNavStep: () => StepId;

  resetCancellationState: () => void;
  completeCancellation: () => void;
  setIsOfferClaimed: (isOfferClaimed: boolean) => void;
  setIsPauseClaimed: (isPauseClaimed: boolean) => void;
  setIsUserAnnualPlan: (isUserAnnualPlan: boolean) => void;
  setIsRedirecting: (isRedirecting: boolean) => void;
  setRedirectPath: (redirectPath: string) => void;
  setFeedbackSubmitted: (feedbackSubmitted: boolean) => void;

  setWarningMessages: (messages: string[]) => void;
  setWarningTitle: (title: string) => void;
  setWarningTemplate: (template: 1 | 2) => void;

  setShuffledOptions: (value: CheckboxOption[]) => void;
};

type TCancellationState = TState & TActions;

const useCancellationStore = create<TCancellationState>((set, get) => ({
  toCancleProductId: "",
  setToCancleProductId: (productId: string) =>
    set({ toCancleProductId: productId }),

  cancellingProductIds: [],
  setCancellingProductIds: (ids: TProductRefId[]) =>
    set({ cancellingProductIds: ids }),

  isProcessing: false,
  setIsProcessing: (isProcessing: boolean) =>
    set({ isProcessing: isProcessing }),

  openCancellationModal: false,
  setOpenCancellationModal: (openCancellationModal: boolean) =>
    set({ openCancellationModal: openCancellationModal }),

  openCancelConfirmModal: false,
  setOpenCancelConfirmModal: (openCancelConfirmModal: boolean) =>
    set({ openCancelConfirmModal: openCancelConfirmModal }),

  missingFeatures: [],
  setMissingFeatures: (features: string[]) =>
    set({ missingFeatures: features }),

  actionType: "none",
  setActionType: (actionType: "cancel" | "downgrade" | "none") =>
    set({ actionType: actionType }),

  feedbackData: {
    reasons: [],
    alternateChoice: "",
    feedback: "",
    subscription_id: "",
    otherfeedback: "",
  },

  setFeedbackData: (data: {
    reasons?: string[];
    alternateChoice?: string;
    feedback?: string;
    otherfeedback?: string;
  }) => {
    set((prev) => ({
      feedbackData: {
        ...prev.feedbackData,
        ...data,
      },
    }));
  },
  isOfferClaimed: false,
  setIsOfferClaimed: (isOfferClaimed: boolean) =>
    set({ isOfferClaimed: isOfferClaimed }),

  isPauseClaimed: false,
  setIsPauseClaimed: (isPauseClaimed: boolean) =>
    set({ isPauseClaimed: isPauseClaimed }),

  isUserAnnualPlan: false,
  setIsUserAnnualPlan: (isUserAnnualPlan: boolean) =>
    set({ isUserAnnualPlan: isUserAnnualPlan }),

  isRedirecting: true,
  setIsRedirecting: (isRedirecting: boolean) =>
    set({ isRedirecting: isRedirecting }),

  redirectPath: "/",
  setRedirectPath: (redirectPath: string) =>
    set({ redirectPath: redirectPath }),

  feedbackSubmitted: false,
  setFeedbackSubmitted: (feedbackSubmitted: boolean) =>
    set({ feedbackSubmitted: feedbackSubmitted }),

  warningMessages: [],
  warningTitle: "Just making sure!",
  warningTemplate: 1,

  setWarningMessages: (messages) => set({ warningMessages: messages }),
  setWarningTitle: (title) => set({ warningTitle: title }),
  setWarningTemplate: (template) => set({ warningTemplate: template }),

  shuffledOptions: [],
  setShuffledOptions: (val) => set({ shuffledOptions: val }),

  // Stack-based navigation ------------- start
  navStack: [...INITIAL_STACK],

  initNavFlow: (startStep: StepId) => {
    set({ navStack: [startStep] });
  },

  pushNavStep: (step: StepId) => {
    set((prev) => ({ navStack: [...prev.navStack, step] }));
  },

  popNavStep: () => {
    const stack = get().navStack;
    if (stack.length <= 1) return null; // can't pop the first step
    const newStack = stack.slice(0, -1);
    set({ navStack: newStack });
    return newStack[newStack.length - 1];
  },

  currentNavStep: () => {
    const stack = get().navStack;
    return stack[stack.length - 1];
  },

  // Stack-based navigation ------------- end

  completeCancellation: () => {
    set((state) => ({
      feedbackSubmitted: true,
      feedbackData: {
        reasons: [],
        alternateChoice: "",
        feedback: "",
        subscription_id: "",
        otherfeedback: "",
      },
      shuffledOptions: [],
      openCancelConfirmModal: false,
    }));
  },

  resetCancellationState: () => {
    set({
      toCancleProductId: "",
      isProcessing: false,
      cancellingProductIds: [],
      navStack: [...INITIAL_STACK],
      openCancelConfirmModal: false,
      missingFeatures: [],
      actionType: "none",
      feedbackData: {
        reasons: [],
        alternateChoice: "",
        feedback: "",
        subscription_id: "",
        otherfeedback: "",
      },
      feedbackSubmitted: false,
      warningMessages: [],
      warningTitle: "Just making sure!",
      warningTemplate: 1,
      shuffledOptions: [],
    });
  },
}));

export default useCancellationStore;
